import type { LocalizedCopy } from "../productCopyTypes";

function paragraphs(text: string): string {
  return text
    .split(/\n\n+/)
    .map((p) => p.trim())
    .filter(Boolean)
    .map((p) => `<p>\n${p}\n</p>`)
    .join("\n\n");
}

function featureList(items: string[]): string {
  return `<ul>\n${items.map((item) => `  <li>${item}</li>`).join("\n")}\n</ul>`;
}

const COPY = {
  en: {
    title: "Car Series",
    description:
      "Car Series is a detailed brick-built off-road vehicle designed for collectors, automotive enthusiasts, and experienced builders. With 876 pieces and an impressive combination of rugged styling and functional elements, this model captures the character of a fully equipped adventure vehicle prepared for demanding terrain.\n\nThe model features a distinctive yellow and black body, large off-road tires, a reinforced front bumper, multiple auxiliary lights, a roof-mounted luggage platform, and detailed exterior equipment. The combination of bright bodywork and dark technical elements gives the vehicle a strong, adventurous appearance.\n\nA major highlight is the model's functionality. The doors can be opened, while the rear features a tow hitch mechanism that allows a compatible trailer or accessory to be connected. Several sections of the vehicle can also be removed or separated, making the construction easier to explore and adding to its modular character.\n\nThe roof rack is loaded with travel equipment, while the sides feature additional storage and accessories. Inside, the model includes a carefully constructed passenger compartment visible through the transparent windows. The detailed front end, lighting elements, mirrors, spare wheel, and other exterior components complete the realistic appearance.\n\nWith 876 pieces, a compact display-friendly size, and a combination of detailed construction and interactive features, Car Series is an excellent addition to an automotive brick collection.",
    highlightsTitle: "Product Features",
    features: [
      "Brand: 12GO",
      "Set Number: 95027",
      "Model: Car Series",
      "Pieces: 876",
      "Recommended Age: 14+",
      "Dimensions: approx. 22.3 × 8.7 × 15.5 cm",
      "Opening doors",
      "Functional rear towing hook",
      "Modular construction",
      "Roof-mounted luggage and equipment",
      "Off-road tires",
      "Detailed front bumper and lighting",
      "Spare wheel",
      "Detailed passenger compartment",
      "Extensive exterior accessories",
      "Designed for building, display, and collection",
    ],
    specsTitle: "Specifications",
    specs: [
      "<li><strong>Brand:</strong> 12GO</li>",
      "<li><strong>Set Number:</strong> 95027</li>",
      "<li><strong>Model:</strong> Car Series</li>",
      "<li><strong>Pieces:</strong> 876</li>",
      "<li><strong>Recommended Age:</strong> 14+</li>",
      "<li><strong>Dimensions:</strong> approx. 22.3 × 8.7 × 15.5 cm</li>",
    ],
    backgroundTitle: "Background",
    background:
      "The design of this vehicle draws on the visual language of classic expedition and off-road vehicles. Its tall body, large tires, robust front end, roof-mounted equipment, and external storage elements are typical of vehicles built for long-distance travel away from paved roads.\n\nRather than focusing on a minimalist sports-car profile, the model emphasizes practicality and equipment. The roof cargo, spare wheel, side-mounted accessories, auxiliary lighting, and reinforced exterior give it the appearance of a vehicle prepared for remote journeys and challenging terrain.\n\nThe brick-built construction translates these features into a compact display model while retaining a high level of visual detail. The modular sections and functional components also make the model more interesting to build and explore than a purely static vehicle.",
  },
  pl: {
    title: "Car Series",
    description:
      "Car Series to szczegółowy model terenowego samochodu wykonany z klocków konstrukcyjnych, przeznaczony dla kolekcjonerów, miłośników motoryzacji oraz bardziej doświadczonych konstruktorów. Zestaw składa się z 876 elementów i łączy charakterystyczną terenową stylistykę z licznymi funkcjonalnymi elementami.\n\nModel wyróżnia się żółto-czarnym nadwoziem, dużymi oponami terenowymi, wzmocnionym przednim zderzakiem, dodatkowymi światłami, bagażnikiem dachowym oraz bogatym wyposażeniem zewnętrznym. Zestawienie jasnego nadwozia z czarnymi elementami technicznymi nadaje mu wyrazisty, ekspedycyjny charakter.\n\nJedną z najciekawszych cech są elementy funkcyjne. Drzwi można otwierać, a z tyłu znajduje się hak holowniczy z mechanizmem umożliwiającym podłączenie kompatybilnej przyczepy lub akcesorium. Poszczególne części konstrukcji można również zdejmować i rozdzielać, co podkreśla modułowy charakter modelu.\n\nNa dachu umieszczono wyposażenie podróżne, natomiast po bokach znajdują się dodatkowe elementy transportowe i akcesoria. Przez przezroczyste szyby można zobaczyć szczegółowo wykonane wnętrze. Całości dopełniają reflektory, lusterka, koło zapasowe, przedni zderzak oraz liczne detale nadwozia.\n\nDzięki 876 elementom, kompaktowym wymiarom oraz połączeniu szczegółowej konstrukcji i funkcjonalności Car Series będzie ciekawym dodatkiem do każdej kolekcji modeli motoryzacyjnych.",
    highlightsTitle: "Cechy produktu",
    features: [
      "Marka: 12GO",
      "Numer zestawu: 95027",
      "Model: Car Series",
      "Liczba elementów: 876",
      "Wiek: 14+",
      "Wymiary: ok. 22,3 × 8,7 × 15,5 cm",
      "Otwierane drzwi",
      "Funkcjonalny tylny hak holowniczy",
      "Modułowa konstrukcja",
      "Bagażnik dachowy z wyposażeniem",
      "Terenowe opony",
      "Szczegółowy przedni zderzak i oświetlenie",
      "Koło zapasowe",
      "Szczegółowe wnętrze",
      "Bogate wyposażenie zewnętrzne",
      "Idealny do budowania, ekspozycji i kolekcjonowania",
    ],
    specsTitle: "Specyfikacja",
    specs: [
      "<li><strong>Marka:</strong> 12GO</li>",
      "<li><strong>Numer zestawu:</strong> 95027</li>",
      "<li><strong>Model:</strong> Car Series</li>",
      "<li><strong>Liczba elementów:</strong> 876</li>",
      "<li><strong>Wiek:</strong> 14+</li>",
      "<li><strong>Wymiary:</strong> ok. 22,3 × 8,7 × 15,5 cm</li>",
    ],
    backgroundTitle: "Tło konstrukcji",
    background:
      "Stylistyka modelu nawiązuje do klasycznych samochodów terenowych i pojazdów ekspedycyjnych przygotowanych do podróży poza utwardzonymi drogami. Wysokie nadwozie, duże opony, solidny przedni pas, wyposażenie dachowe oraz dodatkowe elementy montowane na zewnątrz podkreślają jego terenowy charakter.\n\nZamiast sportowej, aerodynamicznej sylwetki model stawia na praktyczność i funkcjonalność. Bagaż na dachu, koło zapasowe, dodatkowe wyposażenie boczne, światła pomocnicze oraz hak holowniczy tworzą spójną wizję samochodu przeznaczonego do dalekich wypraw.\n\nKonstrukcja z klocków pozwala zachować charakterystyczne cechy takiego pojazdu w kompaktowej formie, jednocześnie oferując kolekcjonerowi wiele szczegółów do odkrycia. Modułowe elementy oraz funkcje ruchome sprawiają, że model jest interesujący zarówno podczas budowania, jak i późniejszej ekspozycji.",
  },
  de: {
    title: "Car Series",
    description:
      "Car Series ist ein detailreiches Klemmbausteinmodell eines robusten Geländefahrzeugs für Sammler, Autoenthusiasten und erfahrene Modellbauer. Mit 876 Teilen verbindet das Modell eine markante Offroad-Optik mit zahlreichen funktionalen und abnehmbaren Elementen.\n\nDas Fahrzeug besitzt eine auffällige gelb-schwarze Farbgebung, große Geländereifen, einen verstärkten Frontbereich, zusätzliche Scheinwerfer, einen Dachgepäckträger und umfangreiche Außenausrüstung. Die Kombination aus heller Karosserie und dunklen technischen Elementen verleiht dem Modell einen ausgeprägten Expeditionscharakter.\n\nZu den Highlights gehören öffnende Türen sowie ein funktionaler Abschlepphaken am Heck, der zum Anbringen eines passenden Anhängers oder Zubehörs verwendet werden kann. Mehrere Baubereiche lassen sich abnehmen oder voneinander trennen, wodurch die modulare Konstruktion besonders gut zur Geltung kommt.\n\nAuf dem Dach befindet sich zusätzliche Reiseausrüstung, während an den Seiten weitere Zubehör- und Stauelemente angebracht sind. Durch die transparenten Scheiben ist der detaillierte Innenraum sichtbar. Scheinwerfer, Spiegel, Ersatzrad und weitere Karosseriedetails runden das Modell ab.\n\nMit 876 Teilen, kompakter Ausstellungsgröße und einer gelungenen Kombination aus Details und Funktionalität ist Car Series eine interessante Ergänzung für jede Sammlung von Fahrzeugmodellen.",
    highlightsTitle: "Produkteigenschaften",
    features: [
      "Marke: 12GO",
      "Setnummer: 95027",
      "Modell: Car Series",
      "Teile: 876",
      "Altersempfehlung: 14+",
      "Abmessungen: ca. 22,3 × 8,7 × 15,5 cm",
      "Öffnende Türen",
      "Funktionaler Abschlepphaken",
      "Modulare Konstruktion",
      "Dachgepäckträger mit Ausrüstung",
      "Geländereifen",
      "Detaillierter Frontbereich und Beleuchtung",
      "Ersatzrad",
      "Detaillierter Innenraum",
      "Umfangreiche Außenausstattung",
      "Für Bau, Ausstellung und Sammlung geeignet",
    ],
    specsTitle: "Spezifikationen",
    specs: [
      "<li><strong>Marke:</strong> 12GO</li>",
      "<li><strong>Setnummer:</strong> 95027</li>",
      "<li><strong>Modell:</strong> Car Series</li>",
      "<li><strong>Teile:</strong> 876</li>",
      "<li><strong>Altersempfehlung:</strong> 14+</li>",
      "<li><strong>Abmessungen:</strong> ca. 22,3 × 8,7 × 15,5 cm</li>",
    ],
    backgroundTitle: "Design-Hintergrund",
    background:
      "Das Design orientiert sich an klassischen Geländefahrzeugen und Expeditionsfahrzeugen, die für Reisen abseits befestigter Straßen ausgestattet sind. Die hohe Karosserie, großen Reifen, der robuste Frontbereich, die Dachausrüstung und die zusätzlichen äußeren Staumöglichkeiten unterstreichen den Offroad-Charakter.\n\nIm Mittelpunkt steht weniger ein sportliches Erscheinungsbild als vielmehr praktische Funktionalität. Dachgepäck, Ersatzrad, seitliche Ausrüstung, Zusatzscheinwerfer und Abschlepphaken vermitteln den Eindruck eines Fahrzeugs für lange Reisen und anspruchsvolles Gelände.\n\nDie Klemmbausteinkonstruktion überträgt diese Eigenschaften in ein kompaktes Sammlermodell und bewahrt dabei zahlreiche sichtbare Details. Die modularen und beweglichen Elemente sorgen dafür, dass das Modell auch nach dem Bau interessant bleibt.",
  },
  fr: {
    title: "Car Series",
    description:
      "Car Series est un modèle en briques détaillé représentant un véhicule tout-terrain robuste, conçu pour les collectionneurs, les passionnés d'automobile et les constructeurs expérimentés. Composé de 876 pièces, il associe une esthétique d'expédition à de nombreux éléments fonctionnels.\n\nLe modèle présente une carrosserie jaune et noire, de larges pneus tout-terrain, un pare-chocs avant renforcé, des feux supplémentaires, une galerie de toit et de nombreux équipements extérieurs.\n\nParmi ses principales fonctionnalités figurent les portes ouvrantes ainsi qu'un crochet de remorquage fonctionnel à l'arrière. Plusieurs sections peuvent également être retirées ou séparées, mettant en valeur la conception modulaire du véhicule.\n\nLa galerie de toit accueille du matériel de voyage, tandis que différents accessoires sont répartis sur les côtés. L'intérieur détaillé reste visible à travers les fenêtres transparentes. Les phares, rétroviseurs, roue de secours et nombreux détails de carrosserie complètent l'ensemble.\n\nAvec ses 876 pièces, son format compact et ses nombreuses fonctions, Car Series constitue une pièce intéressante pour toute collection automobile en briques.",
    highlightsTitle: "Caractéristiques",
    features: [
      "Marque : 12GO",
      "Référence : 95027",
      "Modèle : Car Series",
      "Nombre de pièces : 876",
      "Âge recommandé : 14+",
      "Dimensions : env. 22,3 × 8,7 × 15,5 cm",
      "Portes ouvrantes",
      "Crochet de remorquage fonctionnel",
      "Construction modulaire",
      "Galerie de toit avec équipement",
      "Pneus tout-terrain",
      "Pare-chocs et éclairage détaillés",
      "Roue de secours",
      "Intérieur détaillé",
      "Nombreux accessoires extérieurs",
      "Idéal pour construction, exposition et collection",
    ],
    specsTitle: "Spécifications",
    specs: [
      "<li><strong>Marque :</strong> 12GO</li>",
      "<li><strong>Référence :</strong> 95027</li>",
      "<li><strong>Modèle :</strong> Car Series</li>",
      "<li><strong>Nombre de pièces :</strong> 876</li>",
      "<li><strong>Âge recommandé :</strong> 14+</li>",
      "<li><strong>Dimensions :</strong> env. 22,3 × 8,7 × 15,5 cm</li>",
    ],
    backgroundTitle: "Inspiration du design",
    background:
      "Le design s'inspire des véhicules tout-terrain et des véhicules d'expédition destinés aux déplacements hors des routes goudronnées. La carrosserie surélevée, les grands pneus, la partie avant renforcée et l'équipement extérieur renforcent cette identité.\n\nL'accent est mis sur la fonctionnalité plutôt que sur une silhouette sportive. Les bagages sur le toit, la roue de secours, les accessoires latéraux, les feux supplémentaires et le crochet de remorquage évoquent un véhicule conçu pour les longs trajets et les terrains difficiles.\n\nLa construction en briques permet de reproduire ces caractéristiques sous la forme d'un modèle compact tout en conservant de nombreux détails visuels. Les éléments modulaires et fonctionnels rendent également la construction plus intéressante.",
  },
  es: {
    title: "Car Series",
    description:
      "Car Series es un detallado modelo de bloques que representa un robusto vehículo todoterreno, diseñado para coleccionistas, aficionados al automóvil y constructores experimentados. Con 876 piezas, combina una estética de vehículo de expedición con numerosos elementos funcionales.\n\nEl modelo presenta una llamativa carrocería amarilla y negra, grandes neumáticos todoterreno, un parachoques delantero reforzado, iluminación adicional, una baca de techo y numerosos accesorios exteriores.\n\nLas puertas pueden abrirse y la parte trasera incorpora un gancho de remolque funcional para conectar un accesorio o remolque compatible. Algunas secciones del vehículo también pueden desmontarse, resaltando el carácter modular de la construcción.\n\nLa baca del techo incorpora equipamiento de viaje, mientras que los laterales cuentan con diferentes accesorios y elementos de almacenamiento. El interior detallado puede verse a través de las ventanas transparentes. Los faros, retrovisores, rueda de repuesto y otros detalles exteriores completan el diseño.\n\nCon 876 piezas, unas dimensiones compactas y una combinación de construcción detallada y funciones prácticas, Car Series es una interesante incorporación a cualquier colección automovilística.",
    highlightsTitle: "Características",
    features: [
      "Marca: 12GO",
      "Número de set: 95027",
      "Modelo: Car Series",
      "Piezas: 876",
      "Edad recomendada: 14+",
      "Dimensiones: aprox. 22,3 × 8,7 × 15,5 cm",
      "Puertas practicables",
      "Gancho de remolque funcional",
      "Construcción modular",
      "Baca de techo con equipamiento",
      "Neumáticos todoterreno",
      "Parachoques e iluminación detallados",
      "Rueda de repuesto",
      "Interior detallado",
      "Numerosos accesorios exteriores",
      "Ideal para construcción, exposición y colección",
    ],
    specsTitle: "Especificaciones",
    specs: [
      "<li><strong>Marca:</strong> 12GO</li>",
      "<li><strong>Número de set:</strong> 95027</li>",
      "<li><strong>Modelo:</strong> Car Series</li>",
      "<li><strong>Piezas:</strong> 876</li>",
      "<li><strong>Edad recomendada:</strong> 14+</li>",
      "<li><strong>Dimensiones:</strong> aprox. 22,3 × 8,7 × 15,5 cm</li>",
    ],
    backgroundTitle: "Inspiración del diseño",
    background:
      "El diseño está inspirado en vehículos todoterreno y de expedición preparados para viajar fuera de las carreteras convencionales. La carrocería elevada, los grandes neumáticos, el frontal reforzado y el equipamiento exterior refuerzan su carácter aventurero.\n\nEl modelo prioriza la funcionalidad y el equipamiento. El equipaje del techo, la rueda de repuesto, los accesorios laterales, la iluminación adicional y el gancho de remolque crean la imagen de un vehículo preparado para viajes largos y terrenos exigentes.\n\nLa construcción con bloques permite trasladar estas características a un modelo compacto de exposición, manteniendo una gran cantidad de detalles y elementos interactivos.",
  },
  nl: {
    title: "Car Series",
    description:
      "Car Series is een gedetailleerd bouwstenenmodel van een robuust terreinvoertuig, ontworpen voor verzamelaars, autoliefhebbers en ervaren bouwers. Met 876 onderdelen combineert het model een avontuurlijke uitstraling met verschillende functionele elementen.\n\nHet voertuig heeft een opvallende gele en zwarte carrosserie, grote terreinbanden, een stevige voorbumper, extra verlichting, een dakdrager en diverse externe accessoires.\n\nDe deuren kunnen worden geopend en aan de achterzijde bevindt zich een functionele trekhaak waarmee een passend aanhangsel of accessoire kan worden gekoppeld. Verschillende delen van het voertuig kunnen bovendien worden verwijderd of losgemaakt.\n\nOp het dak bevindt zich reis- en bagageuitrusting, terwijl de zijkanten extra accessoires en opbergruimte bevatten. Het gedetailleerde interieur is door de transparante ramen zichtbaar. Verlichting, spiegels, reservewiel en andere carrosseriedetails maken het geheel compleet.\n\nMet 876 onderdelen, compacte afmetingen en een combinatie van detail en functionaliteit is Car Series een aantrekkelijke aanvulling op een verzameling voertuigmodellen.",
    highlightsTitle: "Productkenmerken",
    features: [
      "Merk: 12GO",
      "Setnummer: 95027",
      "Model: Car Series",
      "Onderdelen: 876",
      "Aanbevolen leeftijd: 14+",
      "Afmetingen: ca. 22,3 × 8,7 × 15,5 cm",
      "Openende deuren",
      "Functionele trekhaak",
      "Modulaire constructie",
      "Dakdrager met uitrusting",
      "Terreinbanden",
      "Gedetailleerde voorbumper en verlichting",
      "Reservewiel",
      "Gedetailleerd interieur",
      "Talrijke externe accessoires",
      "Geschikt voor bouwen, display en collectie",
    ],
    specsTitle: "Specificaties",
    specs: [
      "<li><strong>Merk:</strong> 12GO</li>",
      "<li><strong>Setnummer:</strong> 95027</li>",
      "<li><strong>Model:</strong> Car Series</li>",
      "<li><strong>Onderdelen:</strong> 876</li>",
      "<li><strong>Aanbevolen leeftijd:</strong> 14+</li>",
      "<li><strong>Afmetingen:</strong> ca. 22,3 × 8,7 × 15,5 cm</li>",
    ],
    backgroundTitle: "Ontwerpinspiratie",
    background:
      "Het ontwerp is geïnspireerd op klassieke terrein- en expeditievoertuigen die zijn uitgerust voor reizen buiten verharde wegen. De hoge carrosserie, grote banden, robuuste voorzijde en externe uitrusting benadrukken het avontuurlijke karakter.\n\nPraktische functionaliteit staat centraal. De bagage op het dak, het reservewiel, de zijaccessoires, extra verlichting en trekhaak geven het model het uiterlijk van een voertuig dat is voorbereid op lange reizen en uitdagend terrein.\n\nDe bouwstenenconstructie vertaalt deze kenmerken naar een compact displaymodel, terwijl de modulaire en bewegende elementen extra bouw- en speelwaarde bieden.",
  },
  it: {
    title: "Car Series",
    description:
      "Car Series è un dettagliato modello in mattoncini di un robusto veicolo fuoristrada, progettato per collezionisti, appassionati di automobili e costruttori esperti. Con 876 pezzi, combina uno stile da veicolo d'esplorazione con numerosi elementi funzionali.\n\nIl modello presenta una caratteristica carrozzeria gialla e nera, grandi pneumatici fuoristrada, un paraurti anteriore rinforzato, luci supplementari, portapacchi sul tetto e numerosi accessori esterni.\n\nLe portiere possono essere aperte e sul retro è presente un gancio di traino funzionale per collegare un accessorio o un rimorchio compatibile. Diverse sezioni del veicolo possono inoltre essere rimosse o separate.\n\nSul tetto è presente l'equipaggiamento da viaggio, mentre sui lati sono presenti ulteriori accessori e sistemi di stoccaggio. Gli interni dettagliati sono visibili attraverso i finestrini trasparenti. Fari, specchietti, ruota di scorta e altri dettagli della carrozzeria completano il modello.\n\nCon 876 pezzi, dimensioni compatte e un buon equilibrio tra dettagli e funzionalità, Car Series è una valida aggiunta a qualsiasi collezione automobilistica.",
    highlightsTitle: "Caratteristiche",
    features: [
      "Marca: 12GO",
      "Numero set: 95027",
      "Modello: Car Series",
      "Pezzi: 876",
      "Età consigliata: 14+",
      "Dimensioni: circa 22,3 × 8,7 × 15,5 cm",
      "Portiere apribili",
      "Gancio di traino funzionale",
      "Costruzione modulare",
      "Portapacchi con equipaggiamento",
      "Pneumatici fuoristrada",
      "Paraurti e illuminazione dettagliati",
      "Ruota di scorta",
      "Interni dettagliati",
      "Numerosi accessori esterni",
      "Ideale per costruzione, esposizione e collezione",
    ],
    specsTitle: "Specifiche",
    specs: [
      "<li><strong>Marca:</strong> 12GO</li>",
      "<li><strong>Numero set:</strong> 95027</li>",
      "<li><strong>Modello:</strong> Car Series</li>",
      "<li><strong>Pezzi:</strong> 876</li>",
      "<li><strong>Età consigliata:</strong> 14+</li>",
      "<li><strong>Dimensioni:</strong> circa 22,3 × 8,7 × 15,5 cm</li>",
    ],
    backgroundTitle: "Ispirazione del design",
    background:
      "Il design si ispira ai classici veicoli fuoristrada e da spedizione, equipaggiati per affrontare lunghi viaggi lontano dalle strade asfaltate. La carrozzeria rialzata, i grandi pneumatici, la parte anteriore rinforzata e l'equipaggiamento esterno ne sottolineano il carattere avventuroso.\n\nIl modello punta soprattutto sulla praticità. Il carico sul tetto, la ruota di scorta, gli accessori laterali, le luci supplementari e il gancio di traino ricordano un veicolo preparato per lunghi viaggi e terreni difficili.\n\nLa costruzione in mattoncini riproduce questi elementi in una forma compatta e adatta all'esposizione, mantenendo al tempo stesso numerosi dettagli e funzioni.",
  },
  ru: {
    title: "Car Series",
    description:
      "Car Series — это детализированная модель внедорожника из конструктора, созданная для коллекционеров, любителей автомобилей и опытных конструкторов. Набор состоит из 876 деталей и сочетает выразительный экспедиционный дизайн с различными функциональными элементами.\n\nМодель получила эффектную жёлто-чёрную окраску, крупные внедорожные шины, усиленный передний бампер, дополнительные фары, багажник на крыше и многочисленные внешние аксессуары.\n\nДвери можно открывать, а в задней части установлен функциональный буксировочный крюк, предназначенный для подключения совместимого прицепа или аксессуара. Некоторые элементы конструкции также можно снимать или разъединять.\n\nНа крыше расположено туристическое оборудование, а по бокам представлены дополнительные аксессуары и элементы хранения. Детализированный салон можно рассмотреть через прозрачные окна. Фары, зеркала, запасное колесо и другие детали кузова завершают образ автомобиля.\n\nБлагодаря 876 деталям, компактным размерам и сочетанию высокой детализации с функциональными элементами Car Series станет интересным дополнением автомобильной коллекции.",
    highlightsTitle: "Характеристики",
    features: [
      "Бренд: 12GO",
      "Номер набора: 95027",
      "Модель: Car Series",
      "Количество деталей: 876",
      "Рекомендуемый возраст: 14+",
      "Размеры: примерно 22,3 × 8,7 × 15,5 см",
      "Открывающиеся двери",
      "Функциональный буксировочный крюк",
      "Модульная конструкция",
      "Багажник на крыше с оборудованием",
      "Внедорожные шины",
      "Детализированные передний бампер и освещение",
      "Запасное колесо",
      "Детализированный салон",
      "Многочисленные внешние аксессуары",
      "Подходит для сборки, экспонирования и коллекционирования",
    ],
    specsTitle: "Спецификация",
    specs: [
      "<li><strong>Бренд:</strong> 12GO</li>",
      "<li><strong>Номер набора:</strong> 95027</li>",
      "<li><strong>Модель:</strong> Car Series</li>",
      "<li><strong>Количество деталей:</strong> 876</li>",
      "<li><strong>Рекомендуемый возраст:</strong> 14+</li>",
      "<li><strong>Размеры:</strong> примерно 22,3 × 8,7 × 15,5 см</li>",
    ],
    backgroundTitle: "Особенности дизайна",
    background:
      "Дизайн модели вдохновлён классическими внедорожниками и экспедиционными автомобилями, предназначенными для поездок за пределами обычных дорог. Высокий кузов, крупные колёса, усиленная передняя часть и внешнее оборудование подчёркивают внедорожный характер.\n\nОсновное внимание уделено практичности и оснащению. Багаж на крыше, запасное колесо, боковые аксессуары, дополнительные фары и буксировочный крюк создают образ автомобиля, подготовленного для дальних поездок и сложных условий.\n\nКонструкция из деталей конструктора позволяет сохранить характерные особенности такого автомобиля в компактном формате, одновременно предлагая коллекционеру множество деталей для изучения и демонстрации.",
  },
  zh: {
    title: "Car Series",
    description:
      "Car Series 是一款高细节越野车积木模型，专为收藏家、汽车爱好者以及经验丰富的玩家设计。整套包含 876 个积木零件，将硬朗的越野风格与多种可动及功能结构结合在一起。\n\n模型采用醒目的黄色与黑色车身配色，配备大型越野轮胎、强化前保险杠、辅助灯、车顶行李架以及丰富的外部装备，整体具有非常鲜明的探险车辆风格。\n\n模型的一大亮点是功能性设计。车门可以打开，车尾配备了可连接兼容拖车或配件的拖钩结构。部分车体结构还可以拆卸或分离，使整个模型具有更明显的模块化特点。\n\n车顶搭载旅行装备，两侧还设计了额外的储物和户外配件。通过透明车窗可以看到精细制作的内部空间。车灯、后视镜、备胎以及其他车身细节进一步完善了整体造型。\n\n凭借 876 个零件、适合展示的尺寸以及丰富的细节与功能，Car Series 非常适合作为汽车积木收藏的一部分。",
    highlightsTitle: "产品特点",
    features: [
      "品牌： 12GO",
      "产品编号： 95027",
      "型号： Car Series",
      "积木数量： 876 块",
      "建议年龄： 14 岁以上",
      "成品尺寸： 约 22.3 × 8.7 × 15.5 cm",
      "车门可开启",
      "尾部拖钩可连接",
      "模块化结构",
      "配备车顶行李架及装备",
      "越野轮胎",
      "精细前保险杠与灯光细节",
      "备胎",
      "精细车内空间",
      "丰富外部配件",
      "适合拼搭、展示与收藏",
    ],
    specsTitle: "规格",
    specs: [
      "<li><strong>品牌：</strong>12GO</li>",
      "<li><strong>产品编号：</strong>95027</li>",
      "<li><strong>型号：</strong>Car Series</li>",
      "<li><strong>积木数量：</strong>876 块</li>",
      "<li><strong>建议年龄：</strong>14 岁以上</li>",
      "<li><strong>成品尺寸：</strong>约 22.3 × 8.7 × 15.5 cm</li>",
    ],
    backgroundTitle: "设计背景",
    background:
      "这款模型的设计灵感来源于经典越野车和探险车辆。高车身、大型轮胎、强化车头、车顶装备以及外部储物设施，都体现了为复杂路况和长途探险而准备的车辆特点。\n\n模型并没有采用运动型汽车的设计思路，而是更加注重实用性和装备感。车顶行李、备胎、侧面设备、辅助灯以及拖钩共同构成了完整的探险车辆形象。\n\n通过积木结构，这些典型越野元素被浓缩到一个适合收藏与展示的模型中，同时保留了丰富的细节和可动结构，为拼搭和展示都增加了更多趣味。",
  },
};

type LocaleKey = keyof typeof COPY;

function buildLocaleHtml(locale: LocaleKey): string {
  const c = COPY[locale] ?? COPY.en;
  return `<h1>${c.title}</h1>

${paragraphs(c.description)}

<h2>${c.specsTitle}</h2>

<ul>
${c.specs.join("\n")}
</ul>

<h2>${c.highlightsTitle}</h2>

${featureList([...c.features])}

<h2>${c.backgroundTitle}</h2>

${paragraphs(c.background)}`;
}

export const RICH_PRODUCT_COPY_95027: LocalizedCopy = {
  en: buildLocaleHtml("en"),
  pl: buildLocaleHtml("pl"),
  de: buildLocaleHtml("de"),
  es: buildLocaleHtml("es"),
  fr: buildLocaleHtml("fr"),
  nl: buildLocaleHtml("nl"),
  zh: buildLocaleHtml("zh"),
  it: buildLocaleHtml("it"),
  ru: buildLocaleHtml("ru"),
};
